import { createContext } from 'react';
import styled, { css } from 'styled-components';

export const ResponsiveBootstrapTableWrapper = styled.div`
    .react-bootstrap-table table {
        table-layout: auto;
    }
`;

export const ORIENTATION_PORTRAIT = 'portrait';
export const ORIENTATION_LANDSCAPE = 'landscape';

export const MOBILE_LG = 'mobileLg';
export const TABLET = 'tablet';
export const TABLET_MD = 'tabletMd';
export const TABLET_LG = 'tabletLg';
export const DESKTOP = 'desktop';

export const sizes = {
    desktopLg: 1480,
    desktopMd: 1280,
    desktopSm: 992,
    desktop: 992,
    tablet: 768,
    tabletLg: 1024,
    tabletMd: 640,
    tabletSm: 576,
    phone: 480,
    mobile: 480,
    mobileLg: 480,
    mobileMd: 411,
    mobileSm: 360,
};

const reverseLookupCache = {};

export const ResponsiveContext = createContext({ mediaSize: DESKTOP });

export const onlyIf = (condition) => {
    if (!condition) {
        return () => '';
    }

    return (...args) => css(...args);
};

export const getMediaSize = (
    screenWidth,
    orientation = ORIENTATION_PORTRAIT,
    version2 = false,
) => {
    const screenDetails = `${orientation}-${screenWidth}px-v2:${version2}`;
    let mediaSize;

    if (reverseLookupCache[screenDetails]) {
        return reverseLookupCache[screenDetails];
    } else if (orientation === ORIENTATION_PORTRAIT) {
        if (screenWidth <= sizes.mobileLg) {
            mediaSize = MOBILE_LG;
        } else if (version2 && screenWidth <= sizes.tabletMd) {
            mediaSize = TABLET_MD;
        } else if (screenWidth <= sizes.tablet) {
            mediaSize = TABLET;
        } else if (screenWidth <= sizes.tabletLg) {
            mediaSize = TABLET_LG;
        } else {
            mediaSize = DESKTOP;
        }
    }
    reverseLookupCache[screenDetails] = mediaSize;
    return mediaSize;
};

// Iterate through the sizes and create a media template
const mediaFromSizes = Object.keys(sizes).reduce((acc, label) => {
    acc[label] = (...args) => css`
        @media (max-width: ${sizes[label] / 16}em) {
            ${css(...args)};
        }
    `;
    return acc;
}, {});

export const DesktopOnlyLabel = styled.span`
    ${mediaFromSizes.tablet`
        display: none;
    `}
`;

export const MobileOnlyIcon = styled.span`
    display: none;

    ${mediaFromSizes.tablet`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        line-height: 1;
    `}
`;

export const sizes2 = {
    desktopLg: 1480,
    desktopMd: 1280,
    tabletLg: 1024,
    tablet: 768,
    mobileLg: 480,
};

// Iterate through the sizes and create a media template
export const media2 = Object.keys(sizes2).reduceRight(
    (acc, curSize, index, array) => {
        const lastSize = array[index + 1];
        if (!lastSize) {
            acc[curSize] = (...args) => css`
                @media (max-width: ${sizes2[curSize] / 16}em) {
                    ${css(...args)};
                }
            `;
        } else {
            acc[curSize] = (...args) => css`
                @media (min-width: ${(sizes2[lastSize] + 1) /
                        16}em) and (max-width: ${sizes2[curSize] / 16}em) {
                    ${css(...args)};
                }
            `;
        }

        return acc;
    },
    {},
);

mediaFromSizes.aboveDesktop = (...args) => css`
    @media (min-width: ${(sizes.desktop + 1) / 16}em) {
        ${css(...args)};
    }
`;

mediaFromSizes.aboveTablet = (...args) => css`
    @media (min-width: ${(sizes.tablet + 1) / 16}em) {
        ${css(...args)};
    }
`;

mediaFromSizes.aboveMobile = (...args) => css`
    @media (min-width: ${(sizes.mobile + 1) / 16}em) {
        ${css(...args)};
    }
`;

export const media = mediaFromSizes;
